import VariantPicker from "@theme/variant-picker";
import { Component } from "@theme/component";
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

export class ProductCard extends Component {
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checkedInput?.dataset.variantId || null;
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.#colorCardRevision++;
  }

  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
    requestAnimationFrame(() => {
      this.#updateVariantImages();
    });
  };

  #updateProductUrl(event) {
    // Combined listing: update from newProduct data (works with cache)
    if (event.detail.data.newProduct?.url) {
      this.#setProductUrl(event.detail.data.newProduct.url);

      const featuredMediaUrl = event.detail.resource?.featured_media?.preview_image?.src;
      if (featuredMediaUrl && this.closest("product-card-link")) {
        this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
      }
      return;
    }

    // Cache hit or variant JSON: link the selected variant now, like the section HTML does.
    if (!event.detail.data.html) {
      const variantId = event.detail.resource?.id;
      const currentUrl = this.refs.productCardLink?.getAttribute("href");
      if (variantId && currentUrl) {
        const url = new URL(currentUrl, window.location.href);
        url.searchParams.set("variant", String(variantId));
        this.#setProductUrl(url.pathname + url.search);
      }
      return;
    }

    const anchorElement = event.detail.data.html.querySelector("product-card a");
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector("product-card-link")
      ?.getAttribute("data-featured-media-url");

    if (featuredMediaUrl && this.closest("product-card-link")) {
      this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute("href")?.trim() === "") return;

      this.#setProductUrl(anchorElement.href);
    }
  }

  /**
   * @param {string} productUrl
   */
  #setProductUrl(productUrl) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

    if (productCardLink) productCardLink.href = productUrl;
    if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
    if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;
  }

  // VELMI: parse the compact two-images-per-color map only on interaction.
  #colorCardData;
  #colorCardRevision = 0;

  #getColorCardGroup(event) {
    if (!this.hasAttribute("data-color-card")) return null;
    if (this.#colorCardData === undefined) {
      try {
        this.#colorCardData = JSON.parse(this.querySelector("[data-color-card-media]")?.textContent || "null");
      } catch {
        this.#colorCardData = null;
      }
    }
    const data = this.#colorCardData;
    if (!data?.groups) return null;
    const productId = event?.detail?.data?.newProduct?.id || this.variantPicker?.dataset.productId;
    if (productId && String(productId) !== data.productId) return null;

    const swatch = this.variantPicker?.querySelector('input[type="radio"]:checked');
    const optionId = swatch?.dataset.optionValueId;
    const byId = data.groups.find((group) => group[0] === optionId);
    if (byId) return byId;
    const value = swatch?.value || event?.detail?.resource?.options?.[data.position - 1];
    const color = String(value || "").trim().toLowerCase().replaceAll(" ", "-");
    return data.groups.find((group) => group[1] === color) || null;
  }

  #updateColorCardImages(event) {
    if (!this.hasAttribute("data-color-card")) return false;
    const gallery = this.refs.cardGalleryLink;
    const media = gallery?.closest(".product-card__media");
    const main = gallery?.querySelector(".product-card-main-image");
    if (!media || !main) return false;
    const group = this.#getColorCardGroup(event);

    // An incomplete group may use the native variant image, never a stale hover.
    if (!group?.[2]?.length) {
      this.#colorCardRevision++;
      this.dataset.colorCardSelection = "";
      media.classList.remove("product-card__media--has-second-image");
      gallery.querySelector(".product-card__image--second")?.remove();
      return false;
    }
    if (this.dataset.colorCardSelection === group[0]) return true;

    const revision = ++this.#colorCardRevision;
    this.dataset.colorCardSelection = group[0];
    media.classList.remove("product-card__media--has-second-image");
    gallery.querySelector(".product-card__image--second")?.remove();
    const nextMain = this.#createColorCardImage(group[2][0], main);
    main.replaceWith(nextMain);

    if (this.#colorCardData.hover && group[2][1]) {
      const wrapper = document.createElement("div");
      wrapper.className = "media product-card__image product-card__image--second";
      wrapper.style.cssText = nextMain.parentElement?.style.cssText || "";
      const nextHover = this.#createColorCardImage(group[2][1], nextMain, true);
      // Keep the first image visible while the selected color's hover image loads.
      const reveal = () => {
        if (this.isConnected && revision === this.#colorCardRevision && nextHover.naturalWidth > 0) {
          media.classList.add("product-card__media--has-second-image");
        }
      };
      nextHover.addEventListener("load", reveal, { once: true });
      wrapper.appendChild(nextHover);
      gallery.appendChild(wrapper);
      if (nextHover.complete) reveal();
    }
    return true;
  }

  #createColorCardImage(image, previous, secondary = false) {
    const [source, width, height, alt] = image;
    const img = document.createElement("img");
    img.className = secondary ? "w-full media__image media-hover__element" : previous.className;
    img.alt = alt || "";
    img.width = width;
    img.height = height;
    // This path runs only after choosing a color. No other groups are loaded.
    img.loading = "eager";
    img.decoding = "async";
    img.setAttribute("fetchpriority", secondary ? "low" : "auto");
    // `auto` only applies to lazy images. Keep its media-query fallback here.
    img.sizes = (previous.sizes || "").replace(/^auto(?:\s*,\s*|$)/, "") || "(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw";
    const maxWidth = Math.min(Number(width), 1600);
    const widths = [240, 352, 480, 640, 832, 1200, 1600].filter((size) => size < maxWidth);
    widths.push(maxWidth);
    const url = new URL(source, window.location.href);
    img.srcset = widths.map((size) => {
      url.searchParams.set("width", String(size));
      return `${url.href} ${size}w`;
    }).join(", ");
    url.searchParams.set("width", String(Math.min(maxWidth, 640)));
    img.src = url.href;
    return img;
  }

  #updateVariantImages(event) {
    if (this.#updateColorCardImages(event)) return;
    const mainImage = this.refs.cardGalleryLink?.querySelector(".product-card-main-image");
    if (!mainImage) return;

    const selectedSwatch = this.querySelector('input[type="radio"]:checked');
    if (!selectedSwatch) return;

    // Combined listing: check product match
    const swatchProductId = this.variantPicker?.dataset.productId;
    const currentProductId = this.dataset.productId || this.closest("product-card")?.dataset.productId;
    if (swatchProductId && currentProductId && swatchProductId !== currentProductId) {
      return;
    }

    const selectedLabel = selectedSwatch.closest("label");
    if (!selectedLabel) return;

    // Skip if swatch is hidden
    const swatchItem = selectedLabel.closest(".variant-option__swatch");
    if (swatchItem?.classList.contains("hidden")) return;

    let selectedImage = selectedLabel.querySelector(".media__image");

    // Unavailable variant: if swatch doesn't match event variant, try HTML first
    const swatchVariantId = selectedSwatch.dataset.variantId;
    const eventVariantId = event?.detail.resource?.id;
    if (eventVariantId && swatchVariantId && swatchVariantId !== eventVariantId && event?.detail.data.html) {
      if (this.#updateImageFromHTML(mainImage, event.detail.data.html)) {
        return;
      }
    }

    // Wait for image if not found yet
    if (!selectedImage) {
      const mediaId = selectedLabel.dataset.mediaId || selectedSwatch.dataset.optionMediaId;
      if (mediaId) {
        this.#waitForImageAndUpdate(mainImage, selectedLabel);
      }
      return;
    }

    // Apply image immediately if src exists
    if (selectedImage.src || selectedImage.currentSrc) {
      this.#applyImageToMain(mainImage, selectedImage);
      if (!selectedImage.complete) {
        selectedImage.addEventListener(
          "load",
          () => {
            if (this.#getColorCardGroup()?.[2]?.length) return;
            if (selectedImage.srcset && mainImage.srcset !== selectedImage.srcset) {
              mainImage.srcset = selectedImage.srcset;
            }
          },
          { once: true }
        );
      }
    } else {
      selectedImage.addEventListener(
        "load",
        () => {
          this.#applyImageToMain(mainImage, selectedImage);
        },
        { once: true }
      );
    }
  }

  #waitForImageAndUpdate(mainImage, label) {
    const checkImage = () => {
      const img = label.querySelector(".media__image");
      if (img && (img.complete || img.naturalWidth > 0)) {
        this.#applyImageToMain(mainImage, img);
        return true;
      }
      return false;
    };

    if (checkImage()) return;

    requestAnimationFrame(() => {
      if (checkImage()) return;
      const img = label.querySelector(".media__image");
      if (img) {
        img.addEventListener("load", () => this.#applyImageToMain(mainImage, img), { once: true });
      }
    });
  }

  #updateImageFromHTML(mainImage, html) {
    const newCard = html.querySelector(`product-card[data-product-id="${this.dataset.productId}"]`);
    if (!newCard) return false;

    const newMainImage = newCard.querySelector(".product-card-main-image");
    if (!newMainImage) return false;

    let updated = false;
    if (newMainImage.srcset && mainImage.srcset !== newMainImage.srcset) {
      mainImage.srcset = newMainImage.srcset;
      updated = true;
    }
    if (newMainImage.src && mainImage.src !== newMainImage.src) {
      mainImage.src = newMainImage.src;
      updated = true;
    }
    return updated;
  }

  #applyImageToMain(mainImage, sourceImage) {
    if (!mainImage || !sourceImage) return;
    if (this.#getColorCardGroup()?.[2]?.length) return;

    const newSrcset = sourceImage.srcset || "";
    const newSrc = sourceImage.currentSrc || sourceImage.src;

    if (newSrcset && mainImage.srcset !== newSrcset) {
      mainImage.srcset = newSrcset;
    }
    if (newSrc && mainImage.src !== newSrc) {
      mainImage.src = newSrc;
    }
  }

  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.#updateProductUrl(event);

    if (event.target !== this.variantPicker && event.detail.data.html) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Restore swatches expanded state after morph
    if (this.variantPicker instanceof SwatchesVariantPicker && event.detail.data.html) {
      const expandedKey = this.variantPicker.getStorageKey();
      if (sessionStorage.getItem(expandedKey) === "true") {
        requestAnimationFrame(() => {
          this.variantPicker.restoreExpandedState();
        });
      }
    }

    // Update images: immediate for cache, deferred for cache miss
    if (event.detail.data.isOptimistic || event.detail.data.isBackgroundSync) {
      this.#updateVariantImages(event);
    } else {
      requestAnimationFrame(() => {
        this.#updateVariantImages(event);
      });
    }
  };

  get variantPicker() {
    return this.querySelector("swatches-variant-picker");
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

class SwatchesVariantPicker extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest("product-card");

    // Clear storage on page load (not on morph) - detect by checking if component is new
    if (!this.dataset.initialized) {
      const expandedKey = this.getStorageKey();
      sessionStorage.removeItem(expandedKey);
      this.dataset.initialized = "true";
    }
  }

  updatedCallback() {
    super.updatedCallback?.();

    // Restore expanded state after component update (morph)
    const expandedKey = this.getStorageKey();
    const shouldExpand = sessionStorage.getItem(expandedKey) === "true";

    if (shouldExpand) {
      this.restoreExpandedState();
    }
  }

  getStorageKey() {
    const productId = this.dataset.productId || "unknown";
    return `swatches-expanded-${productId}`;
  }

  restoreExpandedState() {
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    const showMoreButton = this.querySelector(".variant-option__swatch--show-more");

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    if (showMoreButton) showMoreButton.classList.add("hidden");
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes("-swatch");
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || "0");
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Unavailable variant with alternatives: load available variant instead
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const productUrl = this.dataset.productUrl?.split("?")[0];
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", firstAvailableVariantId);
      url.searchParams.set("section_id", "section-rendering-product-card");

      const fetchToken = this.beginVariantFetch();
      const productSelect = this.startProductSelect();
      this.fetchUpdatedSection(url.href, false, false, fetchToken, productSelect);
      return;
    }

    super.variantChanged(event);
  }

  showAllSwatches(event) {
    event?.preventDefault();
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    if (hiddenSwatches.length === 0) return;

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    this.querySelector(".variant-option__swatch--show-more")?.classList.add("hidden");
    sessionStorage.setItem(this.getStorageKey(), "true");
  }
}
if (!customElements.get("swatches-variant-picker")) {
  customElements.define("swatches-variant-picker", SwatchesVariantPicker);
}
